import React from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  CircularProgress,
  FormControlLabel,
  Grid,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { DataGrid, GridColDef } from "@mui/x-data-grid";
import { Link as RouterLink, useNavigate } from "react-router-dom";
import axios from "axios";
import useAuth from "../../hooks/useAuth";
import { apiVariable } from "../../api/apiVariable";
import {
  createDepartmentTicket,
  getAllDepartmentTicketsForHelpers,
  getDepartmentTickets,
} from "../../api/departmentTicketService";
import { EmployeeDto } from "../../interfaces/HR/employee";
import { getStoredEmployeeId } from "../../utils/employeeIdStorage";
import {
  DepartmentTicketPriority,
  DepartmentTicketStatus,
  DepartmentTicketSummaryDto,
} from "../../interfaces/Tickets/departmentTicket";
import { formatDateTimeInChicago } from "../../helpers/formatingFunctions";

export const priorityLabel = (priority: DepartmentTicketPriority) =>
  DepartmentTicketPriority[priority] ?? String(priority);

const departmentTicketStatusLabels: Record<DepartmentTicketStatus, string> = {
  [DepartmentTicketStatus.Open]: "Open",
  [DepartmentTicketStatus.InProgress]: "In Progress",
  [DepartmentTicketStatus.WaitingForSender]: "Waiting for Sender",
  [DepartmentTicketStatus.WaitingForReceiver]: "Waiting for Receiver",
  [DepartmentTicketStatus.Resolved]: "Resolved",
  [DepartmentTicketStatus.Closed]: "Closed",
  [DepartmentTicketStatus.Cancelled]: "Cancelled",
};

const normalizeStatusKey = (value: string) =>
  value.replace(/[^a-z0-9]/gi, "").toLowerCase();

const departmentTicketStatusByKey = Object.entries(
  departmentTicketStatusLabels,
).reduce<Record<string, DepartmentTicketStatus>>(
  (lookup, [statusValue, label]) => {
    const numericStatus = Number(statusValue) as DepartmentTicketStatus;
    lookup[normalizeStatusKey(label)] = numericStatus;
    lookup[normalizeStatusKey(DepartmentTicketStatus[numericStatus])] =
      numericStatus;
    return lookup;
  },
  {},
);

export const statusLabel = (
  status: DepartmentTicketStatus | string | number,
) => {
  if (typeof status === "number") {
    return (
      departmentTicketStatusLabels[status as DepartmentTicketStatus] ??
      String(status)
    );
  }

  if (typeof status === "string") {
    const trimmed = status.trim();
    const numericStatus = Number(trimmed);

    if (!Number.isNaN(numericStatus)) {
      return (
        departmentTicketStatusLabels[numericStatus as DepartmentTicketStatus] ??
        trimmed
      );
    }

    const normalizedStatus =
      departmentTicketStatusByKey[normalizeStatusKey(trimmed)];

    if (normalizedStatus !== undefined) {
      return departmentTicketStatusLabels[normalizedStatus];
    }

    return trimmed;
  }

  return String(status);
};

const employeeName = (employee: EmployeeDto) =>
  [employee.firstName, employee.lastName].filter(Boolean).join(" ") ||
  employee.email ||
  employee.employeeId;

const initialForm = {
  title: "",
  description: "",
  senderDepartment: "",
  receiverDepartment: "",
  priority: DepartmentTicketPriority.Medium,
  assignedUserId: "",
  dueDate: "",
};

const DepartmentTicketsPage = ({
  createMode = false,
}: {
  createMode?: boolean;
}) => {
  const { token, role } = useAuth();
  const navigate = useNavigate();
  const [tickets, setTickets] = React.useState<DepartmentTicketSummaryDto[]>(
    [],
  );
  const [employees, setEmployees] = React.useState<EmployeeDto[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [saving, setSaving] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [form, setForm] = React.useState(initialForm);
  const [showAllTickets, setShowAllTickets] = React.useState(false);
  const isHelper = role?.includes("Helper") ?? false;

  const loadTickets = React.useCallback(async () => {
    if (!token) return;
    setLoading(true);
    setError(null);
    try {
      if (isHelper && showAllTickets) {
        setTickets(await getAllDepartmentTicketsForHelpers(token));
        return;
      }

      const employeeId = getStoredEmployeeId();
      setTickets(await getDepartmentTickets(token, employeeId));
    } catch (err: any) {
      setError(err?.message ?? "Unable to load department tickets.");
    } finally {
      setLoading(false);
    }
  }, [isHelper, showAllTickets, token]);

  React.useEffect(() => {
    loadTickets();
  }, [loadTickets]);

  React.useEffect(() => {
    if (!isHelper && showAllTickets) {
      setShowAllTickets(false);
    }
  }, [isHelper, showAllTickets]);

  React.useEffect(() => {
    const loadEmployees = async () => {
      if (!token || !createMode) return;
      try {
        const { data } = await axios.get<EmployeeDto[]>(
          `${apiVariable}/getAllEmployeesBasicInfo`,
          {
            headers: { Authorization: `Bearer ${token}` },
            params: { areActive: true },
          },
        );
        setEmployees(data);
        try {
          const employeeId = getStoredEmployeeId();
          const currentEmployee = data.find(
            (employee) => employee.id === employeeId,
          );
          if (currentEmployee?.department) {
            setForm((current) => ({
              ...current,
              senderDepartment:
                current.senderDepartment || currentEmployee.department,
            }));
          }
        } catch {
          // The submit action will show a clear message if the employee id is missing.
        }
      } catch {
        setEmployees([]);
      }
    };

    loadEmployees();
  }, [createMode, token]);

  const departments = React.useMemo(() => {
    const ticketDepartments = tickets.flatMap((ticket) => [
      ticket.senderDepartment,
      ticket.receiverDepartment,
    ]);
    const employeeDepartments = employees.map(
      (employee) => employee.department,
    );
    return Array.from(
      new Set([...ticketDepartments, ...employeeDepartments].filter(Boolean)),
    ).sort();
  }, [employees, tickets]);

  const submitTicket = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!token) return;
    setSaving(true);
    setError(null);
    try {
      const employeeId = getStoredEmployeeId();

      const ticket = await createDepartmentTicket(
        {
          title: form.title,
          description: form.description,
          senderDepartment: form.senderDepartment,
          receiverDepartment: form.receiverDepartment,
          priority: Number(form.priority),
          assignedUserId: form.assignedUserId || undefined,
          dueDate: form.dueDate || undefined,
          createdByEmployeeId: employeeId,
        },
        token,
      );
      navigate(`/tickets/${ticket.id}`);
    } catch (err: any) {
      setError(
        err?.response?.data ?? err?.message ?? "Unable to create ticket.",
      );
    } finally {
      setSaving(false);
    }
  };

  const columns: GridColDef[] = [
    { field: "title", headerName: "Title", flex: 1, minWidth: 220 },
    { field: "senderDepartment", headerName: "Sender", width: 160 },
    { field: "receiverDepartment", headerName: "Receiver", width: 160 },
    {
      field: "createdByFullName",
      headerName: "Created By",
      width: 170,
      valueGetter: (_value, row) => row.createdByFullName ?? "Unknown user",
    },
    {
      field: "assignedUserFullName",
      headerName: "Assigned To",
      width: 170,
      valueGetter: (_value, row) => row.assignedUserFullName ?? "Unassigned",
    },
    {
      field: "priority",
      headerName: "Priority",
      width: 120,
      valueFormatter: (value) =>
        priorityLabel(value as DepartmentTicketPriority),
    },
    {
      field: "status",
      headerName: "Status",
      width: 170,
      renderCell: (params) => (
        <Chip label={statusLabel(params.value)} size="small" />
      ),
    },
    {
      field: "dueDate",
      headerName: "Due",
      width: 130,
      valueFormatter: (value) =>
        value ? new Date(value as string).toLocaleDateString() : "",
    },
    {
      field: "lastUpdatedDate",
      headerName: "Updated",
      width: 170,
      valueFormatter: (value) => formatDateTimeInChicago(value),
    },
    {
      field: "actions",
      headerName: "",
      width: 120,
      sortable: false,
      renderCell: (params) => (
        <Button
          component={RouterLink}
          to={`/tickets/${params.id}${showAllTickets ? "?helper=true" : ""}`}
          size="small"
        >
          Open
        </Button>
      ),
    },
  ];

  return (
    <Stack spacing={3}>
      <Box
        flexWrap={{ xs: "wrap", lg: "nowrap" }}
        display="flex"
        justifyContent="space-between"
        alignItems="center"
      >
        <Box>
          <Typography variant="h4">Department Tickets</Typography>
          <Typography color="text.secondary">
            Send, receive, track, discuss, and resolve tickets between
            departments.
          </Typography>
        </Box>
        {!createMode && (
          <Stack
            flexWrap={{ xs: "wrap", lg: "nowrap" }}
            useFlexGap
            direction="row"
            spacing={2}
            alignItems="center"
          >
            {isHelper && (
              <FormControlLabel
                control={
                  <Checkbox
                    checked={showAllTickets}
                    onChange={(event) =>
                      setShowAllTickets(event.target.checked)
                    }
                  />
                }
                label="Show all tickets"
              />
            )}
            <Button
              component={RouterLink}
              to="/tickets/new"
              variant="contained"
            >
              Create Ticket
            </Button>
          </Stack>
        )}
      </Box>
      {error && <Alert severity="error">{error}</Alert>}
      {createMode && (
        <Card component="form" onSubmit={submitTicket}>
          <CardContent>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  required
                  fullWidth
                  label="Title"
                  value={form.title}
                  onChange={(e) => setForm({ ...form, title: e.target.value })}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  required
                  fullWidth
                  multiline
                  minRows={4}
                  label="Description"
                  value={form.description}
                  onChange={(e) =>
                    setForm({ ...form, description: e.target.value })
                  }
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  select={departments.length > 0}
                  required
                  fullWidth
                  label="Sender department"
                  value={form.senderDepartment}
                  onChange={(e) =>
                    setForm({ ...form, senderDepartment: e.target.value })
                  }
                  disabled
                >
                  {departments.map((department) => (
                    <MenuItem key={department} value={department}>
                      {department}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  select={departments.length > 0}
                  required
                  fullWidth
                  label="Receiver department"
                  value={form.receiverDepartment}
                  onChange={(e) =>
                    setForm({ ...form, receiverDepartment: e.target.value })
                  }
                >
                  {departments.map((department) => (
                    <MenuItem key={department} value={department}>
                      {department}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  select
                  fullWidth
                  label="Priority"
                  value={form.priority}
                  onChange={(e) =>
                    setForm({ ...form, priority: Number(e.target.value) })
                  }
                >
                  {Object.values(DepartmentTicketPriority)
                    .filter((value) => typeof value === "number")
                    .map((value) => (
                      <MenuItem key={value} value={value}>
                        {priorityLabel(value as DepartmentTicketPriority)}
                      </MenuItem>
                    ))}
                </TextField>
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  select={employees.length > 0}
                  fullWidth
                  label="Assigned user (optional)"
                  value={form.assignedUserId}
                  onChange={(e) =>
                    setForm({ ...form, assignedUserId: e.target.value })
                  }
                  helperText={
                    employees.length
                      ? "Choose a user by name."
                      : "Start unassigned; assign later if needed."
                  }
                >
                  <MenuItem value="">Unassigned</MenuItem>
                  {employees.map((employee) => (
                    <MenuItem key={employee.id} value={employee.id}>
                      {employeeName(employee)}
                      {employee.department ? ` · ${employee.department}` : ""}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  type="date"
                  label="Due date"
                  InputLabelProps={{ shrink: true }}
                  value={form.dueDate}
                  onChange={(e) =>
                    setForm({ ...form, dueDate: e.target.value })
                  }
                />
              </Grid>
              <Grid item xs={12}>
                <Button type="submit" variant="contained" disabled={saving}>
                  {saving ? "Creating..." : "Create Ticket"}
                </Button>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      )}
      <Card>
        <CardContent>
          {loading ? (
            <CircularProgress />
          ) : (
            <DataGrid
              autoHeight
              rows={tickets}
              columns={columns}
              disableRowSelectionOnClick
              initialState={{
                pagination: { paginationModel: { pageSize: 10 } },
              }}
              pageSizeOptions={[10, 25, 50]}
            />
          )}
        </CardContent>
      </Card>
    </Stack>
  );
};

export default DepartmentTicketsPage;
